// API client for Subcontractor service with dynamic base discovery
export type IdLike = string | number;

export type SubPatch = Partial<{
  name: string;
  verified: boolean;
  rating: number;
  status: 'Active' | 'Inactive' | 'Pending';
  cw_id: string | number | null;
  phone: string;
  email: string;
  website: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  travel_distance: string;
  tm_rate: string;
  uin: string;
  ein: string;
  avatar_url: string | null;
  service_area_geojson: any; // GeoJSON Polygon
  centroid_lat: number | null;
  centroid_lng: number | null;
  pin_color: string; // Hex color code for map pin
}>;

// Always use relative URLs - Traefik routes /subcontractor to backend
const IS_DEV = import.meta.env.DEV;
const ORIGIN = '';  // Always relative, routed by Traefik in production and Vite proxy in dev
const CANDIDATE_BASES = [
  // Common/expected mounts
  '/subcontractor/api/subcontractor',
  '/api/subcontractor',
  '/subcontractor/api',
  '/subcontractor',
  // Defensive permutations (avoid excessive breadth)
  '/api/subcontractor/api/subcontractor',
  '/subcontractor/api/subcontractor/api',
  '/subcontractor/subcontractor',
  '/api/subcontractor/api',
];

let discoveredBase: string | null = null;
let lastDiscoveryFailedAt: number | null = null;

async function probeBase(base: string): Promise<boolean> {
  try {
    // Try OpenAPI spec first (mounted under the router path), then fallback to health path, then docs HTML
    const paths = ['/openapi.json', '/projects', '/docs'];
    for (const p of paths) {
      const url = `${ORIGIN}${base}${p}`;
      const res = await fetch(url, { 
        method: 'GET',
        credentials: 'include'
      });
      if (res?.ok) {
        if (p === '/openapi.json') {
          try {
            const json = await res.json();
            if (json && json.openapi) return true;
          } catch { /* ignore */ }
        } else if (p === '/projects') {
          try {
            const json = await res.json();
            if (json && (json.status === 'OK' || json.database === 'Connected')) return true;
          } catch { return true; }
        } else {
          // /docs typically returns HTML 200
          return true;
        }
      }
    }
    return false;
  } catch {
    return false;
  }
}

let discoveryInFlight: Promise<string> | null = null;
async function discoverApiBase(): Promise<string> {
  // Prefer cached value first
  if (discoveredBase) return discoveredBase;
  if (discoveryInFlight) return discoveryInFlight;
  discoveryInFlight = (async () => {
  // If we recently failed discovery, don't thrash (wait 10s)
  if (lastDiscoveryFailedAt && Date.now() - lastDiscoveryFailedAt < 10_000) {
    // Return current or default without probing
    return discoveredBase || CANDIDATE_BASES[0];
  }

  // Manual override via localStorage
  const override = (typeof localStorage !== 'undefined') ? localStorage.getItem('sm.apiBaseOverride') : null;
  if (override) {
    if (await probeBase(override)) {
      discoveredBase = override;
      try { localStorage.setItem('sm.apiBase', override); } catch {}
      discoveryInFlight = null;
      return discoveredBase!;
    }
  }
  const cached = (typeof localStorage !== 'undefined') ? localStorage.getItem('sm.apiBase') : null;
  if (cached) {
    // Fast-path verify once; if good, use it
    if (await probeBase(cached)) {
      discoveredBase = cached;
      discoveryInFlight = null;
      return discoveredBase!;
    }
  }
  for (const base of CANDIDATE_BASES) {
    const ok = await probeBase(base);
    if (ok) {
      discoveredBase = base;
      try { localStorage.setItem('sm.apiBase', base); } catch {}
      if (import.meta.env.DEV) {
        try { console.info('[api] discovered base', base); } catch {}
      }
      discoveryInFlight = null;
      return base;
    }
  }
  // Default to original assumed base if none probe successfully
  discoveredBase = CANDIDATE_BASES[0];
  discoveryInFlight = null;
  lastDiscoveryFailedAt = Date.now();
  return discoveredBase;
  })();
  return discoveryInFlight;
}

function resetDiscoveredBase() {
  discoveredBase = null;
  try { localStorage.removeItem('sm.apiBase'); } catch {}
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  // Use native fetch for web app
  const method = (init?.method || 'GET').toUpperCase();
  
  const base = await discoverApiBase();
  const url = `${ORIGIN}${base}${path}`;
  
  let resp = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers
    },
    credentials: 'include', // Include cookies for authentication
    body: init?.body
  });
  
  if (import.meta.env.DEV) {
    try { console.debug('[api]', method, url, '->', resp.status); } catch {}
  }
  
  // If 404, the server base may have shifted. Reset and try rediscovery once.
  if (resp && resp.status === 404) {
    resetDiscoveredBase();
    const newBase = await discoverApiBase();
    const retryUrl = `${ORIGIN}${newBase}${path}`;
    resp = await fetch(retryUrl, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers
      },
      credentials: 'include',
      body: init?.body
    });
    if (import.meta.env.DEV) {
      try { console.debug('[api]', method, retryUrl, '(retry) ->', resp.status); } catch {}
    }
  }
  
  if (!resp.ok) {
    let detail: any = undefined;
    try { 
      const text = await resp.text();
      detail = JSON.parse(text);
    } catch { /* ignore */ }
    // If we still fail here with 404, surface a clearer hint
    const hint = resp.status === 404 ? ' (Check API base mount path; set localStorage.sm.apiBaseOverride to the correct base and reload.)' : '';
    const errMsg = `API ${resp.status}${detail && detail.error ? `: ${detail.error}` : ''}${hint}`;
    console.error('[api] Error:', errMsg, 'URL:', url, 'Detail:', detail);
    throw new Error(errMsg);
  }
  
  const text = await resp.text();
  return text ? JSON.parse(text) as T : (undefined as unknown as T);
}

export const api = {
  // Health probe
  health: () => http<{ status: string; database: string; timestamp: string }>(`/projects`),

  // Subcontractors
  listSubcontractors: (params?: { page?: number; pageSize?: number; status?: string; q?: string; }) => {
    const usp = new URLSearchParams();
    if (params?.page) usp.set('page', String(params.page));
    if (params?.pageSize) usp.set('pageSize', String(params.pageSize));
    if (params?.status) usp.set('status', params.status);
    if (params?.q) usp.set('q', params.q);
    const qs = usp.toString();
    return http<{ data: any[]; page: number; pageSize: number }>(`/subcontractors${qs ? `?${qs}` : ''}`);
  },
  getSubcontractor: (id: IdLike) => http<any>(`/subcontractors/${id}`),
  createSubcontractor: (body: Partial<{ name: string; status: string } & Record<string, any>>) =>
    http<any>(`/subcontractors`, { method: 'POST', body: JSON.stringify(body) }),
  updateSubcontractor: (id: IdLike, patch: SubPatch) =>
    http<any>(`/subcontractors/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteSubcontractor: (id: IdLike) => http<{ deleted: true }>(`/subcontractors/${id}`, { method: 'DELETE' }),

  // Vocabularies
  listCerts: () => http<any[]>(`/vocab/certifications`),
  addCert: (name: string) => http<any>(`/vocab/certifications`, { method: 'POST', body: JSON.stringify({ name }) }),
  deleteCert: (id: IdLike) => http<any>(`/vocab/certifications/${id}`, { method: 'DELETE' }),
  listServices: () => http<any[]>(`/vocab/services`),
  addService: (name: string) => http<any>(`/vocab/services`, { method: 'POST', body: JSON.stringify({ name }) }),
  deleteService: (id: IdLike) => http<any>(`/vocab/services/${id}`, { method: 'DELETE' }),

  // Linking
  linkCertification: (id: IdLike, certification: string | number) =>
    http<any>(`/subcontractors/${id}/certifications`, { method: 'POST', body: JSON.stringify({ certification, certification_id: certification }) }),
  unlinkCertification: (id: IdLike, certId: IdLike) =>
    http<any>(`/subcontractors/${id}/certifications/${certId}`, { method: 'DELETE' }),
  linkService: (id: IdLike, service: string | number) =>
    http<any>(`/subcontractors/${id}/services`, { method: 'POST', body: JSON.stringify({ service, service_id: service }) }),
  unlinkService: (id: IdLike, serviceId: IdLike) =>
    http<any>(`/subcontractors/${id}/services/${serviceId}`, { method: 'DELETE' }),

  // Tickets
  listTickets: (id: IdLike) => http<any[]>(`/subcontractors/${id}/tickets`),
  createTicket: (id: IdLike, payload: { title: string; external_ref?: string; event_date?: string }) =>
    http<any>(`/subcontractors/${id}/tickets`, { method: 'POST', body: JSON.stringify(payload) }),

  // Attachments (metadata only)
  listAttachments: (id: IdLike) => http<any[]>(`/subcontractors/${id}/attachments`),
  upsertAttachment: (id: IdLike, payload: { file_name: string; content_type?: string; byte_size?: number; storage_key: string; expiration_date?: string; sha256_hex?: string; category?: string }) =>
    http<any>(`/subcontractors/${id}/attachments`, { method: 'POST', body: JSON.stringify(payload) }),
  deleteAttachment: (id: IdLike, attachmentId: IdLike) =>
    http<any>(`/subcontractors/${id}/attachments/${attachmentId}`, { method: 'DELETE' }),

  // ConnectWise integration
  cw: {
    // Companies
    listCompanies: (pageSize?: number, maxPages?: number) => {
      const params = new URLSearchParams();
      if (pageSize) params.set('pageSize', String(pageSize));
      if (maxPages) params.set('maxPages', String(maxPages));
      const query = params.toString();
      return fetch(`/api/cw/companies${query ? '?' + query : ''}`, { credentials: 'include' })
        .then(r => r.json());
    },
    upsertCompanyForSub: (payload: { id?: string | number; name: string; basic?: any; latLong?: string }) =>
      fetch('/api/cw/companies', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(r => r.json()),
    getCompany: (id: number) =>
      fetch(`/api/cw/companies/${id}`, { credentials: 'include' }).then(r => r.json()),
    deleteCompany: (id: number, expectName?: string) => {
      const params = new URLSearchParams();
      if (expectName) params.set('expectName', expectName);
      const query = params.toString();
      return fetch(`/api/cw/companies/${id}${query ? '?' + query : ''}`, {
        method: 'DELETE',
        credentials: 'include'
      }).then(r => r.json());
    },

    // Tickets
    getTicket: (id: number) =>
      fetch(`/api/cw/tickets/${id}`, { credentials: 'include' }).then(r => r.json()),
    searchTickets: (conditions?: string, page?: number, pageSize?: number) =>
      fetch('/api/cw/tickets/search', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conditions, page, pageSize })
      }).then(r => r.json()),
    createTicket: (payload: any) =>
      fetch('/api/cw/tickets', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(r => r.json()),

    // Company-specific queries
    listCompanyTickets: (companyId: number, href?: string, pageSize?: number, page?: number) => {
      const params = new URLSearchParams();
      if (href) params.set('href', href);
      if (pageSize) params.set('pageSize', String(pageSize));
      if (page) params.set('page', String(page));
      const query = params.toString();
      return fetch(`/api/cw/companies/${companyId}/tickets${query ? '?' + query : ''}`, {
        credentials: 'include'
      }).then(r => r.json());
    },
    listCompanyNotes: (companyId: number, href?: string, pageSize?: number, page?: number) => {
      const params = new URLSearchParams();
      if (href) params.set('href', href);
      if (pageSize) params.set('pageSize', String(pageSize));
      if (page) params.set('page', String(page));
      const query = params.toString();
      return fetch(`/api/cw/companies/${companyId}/notes${query ? '?' + query : ''}`, {
        credentials: 'include'
      }).then(r => r.json());
    },
    getCompanyPrimaryContact: (companyId: number, href?: string) => {
      const params = new URLSearchParams();
      if (href) params.set('href', href);
      const query = params.toString();
      return fetch(`/api/cw/companies/${companyId}/contact${query ? '?' + query : ''}`, {
        credentials: 'include'
      }).then(r => r.json());
    },
    listCompanyPurchaseOrders: (companyId: number, href?: string, pageSize?: number, page?: number) => {
      // Use the new search endpoint with vendor company filter
      // Try status/name for the status field (ConnectWise lookup pattern)
      //const conditions = `vendorCompany/id=${companyId} AND (status/name="Subcontractor Ordered" OR status/name="Waiting Subcontractor Invoice")`;
      const conditions = `vendorCompany/id=${companyId}`;
      return fetch('/api/cw/purchase-orders/search', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          conditions,
          page: page || 1,
          pageSize: pageSize || 5,
          orderBy: 'poDate desc'
        })
      }).then(r => r.json());
    }
  }
};

export function isNumericId(id: unknown): id is string | number {
  if (id === null || id === undefined) return false;
  const s = String(id);
  return /^\d+$/.test(s);
}

export function isUuidId(id: unknown): id is string {
  if (id === null || id === undefined) return false as never;
  const s = String(id);
  // UUID v1-5 pattern (accept upper/lowercase)
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(s);
}
